import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Clock, Users, ChevronDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toLocalDateString, projectAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { TASKS, DEFAULT_HOURS, hoursToTimeStr, timeStrToHours } from '../utils/timeEntries';

const TimeEntryForm = ({ projects, employees, onSaved }) => {
  const { toast } = useToast();
  const [date, setDate] = useState(toLocalDateString(new Date()));
  const [projectId, setProjectId] = useState('');
  const [task, setTask] = useState('');
  const [comment, setComment] = useState('');
  const [problem, setProblem] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [hoursById, setHoursById] = useState({});
  const [showPicker, setShowPicker] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Un pointage n'a de sens que sur un chantier réellement EN COURS sur le
  // terrain (pas un chantier seulement planifié).
  const activeProjects = projects
    .filter((p) => p.status === 'in-progress')
    .sort((a, b) => a.name.localeCompare(b.name));

  const toggleEmployee = (id) => {
    setSelectedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (!prev.includes(id) && hoursById[id] === undefined) {
        setHoursById((h) => ({ ...h, [id]: hoursToTimeStr(DEFAULT_HOURS) }));
      }
      return next;
    });
  };

  const selectedEmployees = employees.filter((e) => selectedIds.includes(e.id));
  const employeeLabel = selectedIds.length === 0
    ? 'Sélectionner un ou plusieurs salariés'
    : selectedIds.length === 1
      ? selectedEmployees[0]?.fullName
      : `${selectedIds.length} salariés sélectionnés`;

  const canSave = date && projectId && task && selectedIds.length > 0
    && selectedIds.every((id) => timeStrToHours(hoursById[id]) > 0);

  const resetForm = () => {
    setProjectId(''); setTask(''); setComment(''); setProblem(false);
    setSelectedIds([]); setHoursById({});
  };

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    try {
      const result = await projectAPI.addTimeEntries({
        date,
        projectId,
        task,
        comment: comment || null,
        problem,
        lines: selectedIds.map((id) => ({
          employeeId: id,
          hours: timeStrToHours(hoursById[id]),
        })),
      });
      toast({
        title: 'Heures enregistrées',
        description: `${result.rows} ligne(s) enregistrée(s)`,
        duration: 4000,
      });
      resetForm();
      if (onSaved) onSaved();
    } catch (error) {
      toast({
        title: "Échec de l'enregistrement",
        description: error?.response?.data?.detail || String(error),
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="border-blue-200">
      <CardHeader>
        <CardTitle className="flex items-center text-blue-800">
          <Clock className="w-5 h-5 mr-2" />
          Ajouter des heures
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="te-date" className="text-blue-700">Date</Label>
            <Input id="te-date" type="date" value={date}
                   onChange={(e) => setDate(e.target.value)}
                   className="border-blue-200" />
          </div>

          <div className="space-y-2">
            <Label className="text-blue-700">Chantier</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="border-blue-200">
                <SelectValue placeholder="Sélectionner un chantier" />
              </SelectTrigger>
              <SelectContent>
                {activeProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-blue-700">Tâche</Label>
          <Select value={task} onValueChange={setTask}>
            <SelectTrigger className="border-blue-200">
              <SelectValue placeholder="Sélectionner une tâche" />
            </SelectTrigger>
            <SelectContent>
              {TASKS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-blue-700 flex items-center">
            <Users className="w-4 h-4 mr-1" />
            Salariés et heures
          </Label>
          <Popover open={showPicker} onOpenChange={setShowPicker}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-between border-blue-200 font-normal text-left">
                <span className={selectedIds.length ? 'text-blue-900' : 'text-muted-foreground'}>
                  {employeeLabel}
                </span>
                <ChevronDown className="w-4 h-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <div className="max-h-64 overflow-y-auto py-1">
                {employees.filter((e) => e.active !== false).map((emp) => (
                  <label key={emp.id} htmlFor={`te-emp-${emp.id}`}
                         className="flex items-center space-x-2 px-3 py-1.5 cursor-pointer hover:bg-blue-50">
                    <Checkbox id={`te-emp-${emp.id}`}
                              checked={selectedIds.includes(emp.id)}
                              onCheckedChange={() => toggleEmployee(emp.id)} />
                    <span className="text-sm text-blue-900 flex-1">{emp.fullName}</span>
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          {selectedEmployees.length > 0 && (
            <div className="border border-blue-100 rounded-lg divide-y divide-blue-50">
              {selectedEmployees.map((emp) => (
                <div key={emp.id} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-blue-900">{emp.fullName}</span>
                  <Input
                    type="time"
                    value={hoursById[emp.id] ?? hoursToTimeStr(DEFAULT_HOURS)}
                    onChange={(e) => setHoursById((h) => ({ ...h, [emp.id]: e.target.value }))}
                    className="w-28 h-8 border-blue-200"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="te-comment" className="text-blue-700">Commentaire (optionnel)</Label>
          <Textarea id="te-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                     className="border-blue-200" rows={2} />
        </div>

        <label htmlFor="te-problem" className="flex items-center space-x-2 cursor-pointer">
          <Checkbox id="te-problem" checked={problem} onCheckedChange={setProblem} />
          <span className="text-sm text-blue-800 flex items-center">
            <AlertTriangle className="w-3.5 h-3.5 mr-1 text-orange-500" />
            Problème à signaler
          </span>
        </label>

        <Button onClick={handleSave} disabled={!canSave || isSaving}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {isSaving ? 'Enregistrement...' : 'Enregistrer les heures'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default TimeEntryForm;
